import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

const NAV_LINKS = [
  { id: 'showcase',     label: { es: 'Demostración', en: 'Showcase' } },
  { id: 'features',     label: { es: 'Plataforma',   en: 'Platform' } },
  { id: 'how',          label: { es: 'Proceso',      en: 'Process' } },
  { id: 'compliance',   label: { es: 'Cumplimiento', en: 'Compliance' } },
  { id: 'likeness',     label: { es: 'Permisos',     en: 'Permissions' } },
  { id: 'impact',       label: { es: 'Impacto',      en: 'Impact' } },
  { id: 'pricing',      label: { es: 'Precios',      en: 'Pricing' } },
  { id: 'faq',          label: { es: 'Preguntas',    en: 'FAQ' } },
];

export default function NavLando() {
  const { content, locale, setLocale } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const site = content.site || {};
  const brand = site.brand_name || 'MODARIA';
  const appUrl = site.app_login_url || '#';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  return (
    <header
      data-testid="nav-bar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-bg/70 backdrop-blur-xl border-b border-line' : 'bg-transparent'
      }`}
    >
      <div className="container-x flex items-center justify-between py-4 px-5 md:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="text-block text-2xl md:text-3xl tracking-tight text-ink leading-none">{brand}</span>
          <span className="hidden md:inline-block w-1.5 h-1.5 rounded-full bg-acid group-hover:scale-150 transition-transform" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-[12px] font-mono uppercase tracking-[0.18em] text-ink-muted">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className="hover:text-ink transition-colors relative group"
            >
              {pickLocale(l.label, locale)}
              <span className="absolute -bottom-1 left-0 right-0 h-px bg-acid scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.2em]">
            <button data-testid="lang-es" onClick={() => setLocale('es')} className={`px-2 py-1 rounded ${locale === 'es' ? 'text-acid' : 'text-ink-muted hover:text-ink'}`}>ES</button>
            <span className="text-ink-dim">/</span>
            <button data-testid="lang-en" onClick={() => setLocale('en')} className={`px-2 py-1 rounded ${locale === 'en' ? 'text-acid' : 'text-ink-muted hover:text-ink'}`}>EN</button>
          </div>

          <a
            data-testid="nav-cta-access"
            href={appUrl}
            target="_blank" rel="noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-[0.2em] text-ink-muted hover:text-acid transition-colors"
          >
            {locale === 'es' ? 'Acceder' : 'Sign in'} <ArrowUpRight size={14} />
          </a>

          <button
            data-testid="nav-cta-demo"
            onClick={() => goSection('contact')}
            className="hidden md:inline-flex btn-acid !px-5 !py-2.5 !text-[10px]"
          >
            {locale === 'es' ? 'Demo' : 'Demo'}
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-ink"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-bg-secondary border-t border-line"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => goSection(l.id)}
                  className="text-left text-2xl text-block text-ink py-1"
                >
                  {pickLocale(l.label, locale)}
                </button>
              ))}
              <div className="flex items-center gap-3 pt-3 border-t border-line">
                <button onClick={() => setLocale('es')} className={`px-3 py-1 rounded text-xs uppercase font-mono ${locale === 'es' ? 'text-acid' : 'text-ink-muted'}`}>ES</button>
                <button onClick={() => setLocale('en')} className={`px-3 py-1 rounded text-xs uppercase font-mono ${locale === 'en' ? 'text-acid' : 'text-ink-muted'}`}>EN</button>
                <a href={appUrl} target="_blank" rel="noreferrer" className="ml-auto text-xs uppercase font-mono text-acid">{locale === 'es' ? 'Acceder' : 'Sign in'} ↗</a>
              </div>
              <button onClick={() => goSection('contact')} className="btn-acid mt-2 justify-center">{locale === 'es' ? 'Solicitar demo' : 'Request demo'}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
